'use client';

import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { useTranslations } from 'next-intl';
import Link from 'next/link';

import Avatar from '@/components/Avatar';
import Form from '@/components/Forms/Form';
import FormCheckbox from '@/components/Forms/FormCheckbox';
import FormFileUpload from '@/components/Forms/FormFileUpload';
import FormInput from '@/components/Forms/FormInput';
import Clock from '@/components/SvgIcons/Clock';
import OpenLetterCheck from '@/components/SvgIcons/OpenLetterCheck';
import { Link as LinkTranslation } from '@/i18n/navigation';
import colors from '@/styles/themes/colors';
import useToggleState from '@/utils/hooks/useToggleState';
import { FormValidator } from '@/utils/static/FormValidator';

import styles from './ContactSection.module.scss';

export interface ContactFormValues {
  fullName: string;
  email: string;
  message: string;
  consent: boolean;
  file: File | null;
}

const defaultValues: ContactFormValues = {
  fullName: '',
  email: '',
  message: '',
  consent: false,
  file: null,
};

const renderPrivacyPolicyLink = (chunks: React.ReactNode) => (
  <LinkTranslation href="/privacy-policy" target="_blank" className={styles.privacyLink}>
    {chunks}
  </LinkTranslation>
);

const ContactSection = () => {
  const [successState, toggleSuccessState] = useToggleState();
  const t = useTranslations('contact');

  const consentLabel = (
    <>{t.rich('form.consent', { link: (chunks: React.ReactNode) => renderPrivacyPolicyLink(chunks) })}</>
  );

  const handleSubmit = async (formValues: ContactFormValues) => {
    try {
      const formData = new FormData();

      formData.append('fullName', formValues.fullName);
      formData.append('email', formValues.email);
      formData.append('message', formValues.message);

      if (formValues.file) {
        formData.append('file', formValues.file);
      }

      const response = await fetch('/api/contact', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Error sending email');
      }

      toggleSuccessState();
    } catch (error) {
      /* eslint-disable-next-line no-console */
      console.error('An error occurred:', error);
      throw error;
    }
  };

  const renderCalendlySection = () => (
    <Stack alignItems="center" className={styles.calendlySection}>
      <Typography variant="subtitle2" component="p" color={colors.white}>
        {t('calendly.or')}
      </Typography>
      <Stack className={styles.calendlyCard} alignItems="center" gap={3}>
        <Stack alignItems="center" gap={2} className={styles.calendlyInfo}>
          <Box className={styles.ellipse} />
          <Link href="https://www.linkedin.com/in/ilovri00" target="_blank" className={styles.profileLink}>
            <Stack direction="row" alignItems="center" position="relative" zIndex={3} gap={2}>
              <Avatar
                name={t('calendly.name')}
                role={t('calendly.role')}
                image="/images/avatars/ivan.webp"
                size="xl"
                className={styles.avatar}
              />
              <Stack>
                <Typography variant="subtitle1" component="p" fontWeight={500} color={colors.white}>
                  {t('calendly.name')}
                </Typography>
                <Typography variant="body1" color={colors.graytone200} fontWeight={400}>
                  {t('calendly.role')}
                </Typography>
              </Stack>
            </Stack>
          </Link>
          <Typography variant="body1" color={colors.graytone300} textAlign="center" position="relative" zIndex={3}>
            {t('calendly.description')}
          </Typography>
        </Stack>
        <Stack alignItems="center" gap={2} width="100%">
          <Link
            href="https://calendly.com/ivan-workspace/new-meeting"
            target="_blank"
            className={styles.calendlyButton}
          >
            <Button size="large" fullWidth>
              {t('calendly.button')}
            </Button>
          </Link>
          <Stack direction="row" alignItems="center" gap={1} className={styles.timezone}>
            <Clock fill={colors.graytone400} size={20} />
            <Typography variant="body2" color={colors.graytone400}>
              {t('calendly.timezone')}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );

  const renderForm = () => (
    <Stack alignItems="center" className={styles.formWrapper}>
      <Form defaultValues={defaultValues} onSubmit={handleSubmit} mode="onBlur" className={styles.form}>
        {({ formState: { isSubmitting } }) => (
          <Stack gap={3} width="100%">
            <Typography variant="h4" component="h2">
              {t('form.title')}
            </Typography>
            <FormInput name="fullName" label={t('form.name')} validate={FormValidator.isNotEmpty} />
            <FormInput
              name="email"
              type="email"
              label={t('form.email')}
              validate={FormValidator.all(FormValidator.isNotEmpty, FormValidator.isValidEmail)}
            />
            <FormInput name="message" label={t('form.message')} multiline autoGrow minRows={1} />
            <Stack gap={2}>
              <Typography variant="body1">{t('form.filesLabel')}</Typography>
              <FormFileUpload name="file" label={t('form.uploadFiles')} accept=".pdf,.doc,.docx" maxSize={5} />
            </Stack>
            <FormCheckbox name="consent" label={consentLabel} validate={FormValidator.isChecked} />
            <Button type="submit" size="large" disabled={isSubmitting} fullWidth>
              {isSubmitting ? t('form.sending') : t('form.submit')}
            </Button>
          </Stack>
        )}
      </Form>
      {renderCalendlySection()}
    </Stack>
  );

  const renderSuccessState = () => (
    <Stack alignItems="center" className={styles.formWrapper}>
      <Stack justifyContent="center" alignItems="center" className={styles.successBox}>
        <Stack alignItems="center" className={styles.iconWrapper}>
          <OpenLetterCheck fill={colors.blue500} size={52} />
        </Stack>
        <Typography variant="h3" component="p" textAlign="center" color={colors.white}>
          {t('successState.title')}
        </Typography>
        <Typography variant="body1" color={colors.graytone300} textAlign="center">
          {t('successState.text')}
        </Typography>
        <Link href="/">
          <Button size="large" fullWidth>
            {t('successState.button')}
          </Button>
        </Link>
      </Stack>
    </Stack>
  );

  return (
    <Container component="section" maxWidth="xl" className={styles.container}>
      {successState ? renderSuccessState() : renderForm()}
    </Container>
  );
};

export default ContactSection;
